import type { MCPTool } from "@/types";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { useMemo } from "react";
import React from "react";

interface ToolsToggleProps {
  tools: MCPTool[];
  onChange: (tools: MCPTool[]) => void;
}

export const ToolsToggle = ({ tools, onChange }: ToolsToggleProps) => {
  const sorted = useMemo(() => [...tools].sort((a, b) => a.name.localeCompare(b.name)), [tools]);

  const handleToggle = (name: string) => {
    const next = sorted.map(tool =>
      tool.name === name ? { ...tool, enabled: tool.enabled === false ? true : false } : tool,
    );
    onChange(next);
  };

  // Close on Escape key
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onChange(sorted);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onChange, sorted]);

  return (
    <div className="w-72 rounded-xl border border-border/60 bg-white/90 p-3 shadow-lg dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">Tools</p>
        <span className="text-xs text-muted-foreground">{tools.length} available</span>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {sorted.map(tool => (
          <label
            key={tool.name}
            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-muted"
          >
            <Checkbox
              checked={tool.enabled !== false}
              onChange={() => handleToggle(tool.name)}
              aria-label={`Toggle ${tool.name}`}
            />
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-foreground">{tool.name}</p>
              {tool.description ? (
                <p className="text-xs text-muted-foreground line-clamp-2">{tool.description}</p>
              ) : null}
            </div>
          </label>
        ))}
        {sorted.length === 0 ? <p className="text-xs text-muted-foreground">No tools available.</p> : null}
      </div>
      <div className="mt-3 flex justify-end">
        <Button type="button" size="sm" variant="outline" onClick={() => onChange(sorted)}>
          Close
        </Button>
      </div>
    </div>
  );
};
